import React from 'react';
import { useTranslate } from 'nucleus-text';
import { v4 as uuidv4 } from 'uuid';
import useBreakpoints from '@hooks/useBreakpoints';
import { CSSObject } from '@emotion/css';
import { Alerts } from '@cvent/carina/components/Alert';
import { errorTypes } from './ApptFileUpload';

interface Error {
  type: string;
  files: string[];
}
interface Alerts {
  errors: Error[];
}

const useStyle = (): Record<string, CSSObject> => {
  const { isM } = useBreakpoints();
  return {
    alert: {
      zIndex: 1000,
      position: 'absolute',
      marginRight: 24,
      marginTop: 92,
      overflowWrap: 'anywhere',
      transform: isM ? 'translate(30%)' : 'translate(0)'
    }
  };
};

export function ApptFileUploadError({ errors }: Alerts): JSX.Element {
  const { translate } = useTranslate();
  const { alert } = useStyle();

  let isSingleFile: boolean;
  const alerts = errors
    .map(error => {
      isSingleFile = error.files.length === 1;
      let message;
      switch (error.type) {
        case errorTypes.TOO_MANY_FILES: // This alert is not shown with list of files, hence isSingleFile = true.
        case errorTypes.FILE_NUM_EXCEEDS_LIMIT:
          message = translate('AppointmentPanel-FileUploadAlert-FileLimitExceed');
          isSingleFile = true;
          break;
        case errorTypes.FILE_TOO_LARGE:
          if (isSingleFile) {
            message = translate('AppointmentPanel-FileUploadAlert-SingleFileSizeExceed', { fileName: error.files });
          } else {
            message = translate('AppointmentPanel-FileUploadAlert-MultipleFileSizeExceed');
          }
          break;
        case errorTypes.FILE_INVALID_TYPE:
          if (isSingleFile) {
            message = translate('AppointmentPanel-FileUploadAlert-SingleFileInvalidType', {
              fileName: error.files
            });
          } else {
            message = translate('AppointmentPanel-FileUploadAlert-MultipleFileInvalidType');
          }
          break;
        default:
          break;
      }

      if (message) {
        const contents = isSingleFile ? (
          message
        ) : (
          <div>
            <div>{message}</div>
            <ul style={{ fontSize: 'inherit' }}>
              {error.files.map(fileName => (
                <li key={uuidv4()}>{fileName}</li>
              ))}
            </ul>
          </div>
        );

        return {
          appearance: 'danger',
          isPositionFixed: false,
          content: contents,
          dismissible: true
        };
      }
      return null;
    })
    .filter(Boolean);

  return (
    <div css={alert}>
      <Alerts variant="user" alerts={alerts} />
    </div>
  );
}
